import React, { useEffect, useState } from 'react';

import {
  ActionIcon,
  Badge,
  Checkbox,
  Divider,
  Group,
  Input,
  LoadingOverlay,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
  createStyles,
  px,
} from '@mantine/core';
import { SelectItem } from '@mantine/core/lib/Select/types';
import { DatePickerInput } from '@mantine/dates';
import { isNotEmpty, useForm } from '@mantine/form';
import { useDisclosure, useShallowEffect } from '@mantine/hooks';
import { IconAlertTriangleFilled, IconPlus } from '@tabler/icons-react';
import { AxiosError, AxiosResponse } from 'axios';
import ExpandableSection from 'components/ExpandableSection';
import DocumentAutoComplete, {
  DocumentAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/DocumentAutoComplete';
import PackageCategorySelect from 'components/form-fields/auto-complete-fields/PackageCategorySelect';
import AthleteStatusSelect, {
  athleteStatusOptions,
} from 'components/form-fields/select-fields/AthleteStatusSelect';
import PackageSelect from 'components/form-fields/select-fields/PackageSelect';
import HelpButton from 'components/HelpButton';
import WarningButton from 'components/WarningButton';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import usePackage from 'hooks/package/usePackage';
import { UseMutationResult, UseQueryResult } from 'react-query';
import { ErrorMessage, Package, PackageRetrieve, PackageWrite } from 'types';
import { computeTotalValidity } from 'utils/billing.helpers';
import { parseDateTimeToDate } from 'utils/date-functions';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

import RenewalSummary from './components/RenewalSummary';
import TimeBasedAttendanceRuleCard from './components/TimeBasedAttendanceRuleCard';
import {
  validateAthleteEndStatus,
  validateEventTypes,
  validateHasLimitedAttendanceRule,
} from './formValidators';
import { PERIODOPTIONS, Period, TimeBasedPackageFormValues } from './types';
import PackageDrawerState, { PackageType } from '../../types';

const useStyles = createStyles(theme => ({
  packageForm: {
    overflowY: 'scroll',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  top: {
    display: 'grid',
    gridTemplateColumns: 'max-content auto',
    gap: theme.spacing.md,
    justifyItems: 'stretch',
    alignItems: 'start',
  },
}));

const TIME_OR_CREDIT_BASED_LIMITED_RULE_MESSAGE =
  'A time or credit-based package needs at least one attendance rule with a credit count of one or more.';

const validateValidityIntervalCount = (
  value: number | null,
  values: TimeBasedPackageFormValues,
  path: string,
): string | null => {
  const attendanceRuleIndex = Number(path.split('.')[1]);
  const interval = values.attendanceRules[attendanceRuleIndex].interval;
  const intervalCount = values.attendanceRules[attendanceRuleIndex].intervalCount;

  if (!value) return 'Validity length must be set.';

  if (interval === null || intervalCount === null) return null;

  if (value % intervalCount !== 0) return 'Validity length must be divisible by attendance rule length.';

  if (value / intervalCount > 60)
    return 'Validity length must not exceed 60 times the attendance rule length.';

  return null;
};

interface IPackageTypeSettings {
  allowsProration: boolean;
  allowsFollowUpPackage: boolean;
  renewsOnCredits: boolean;
}

/** Which settings the time-based form offers for each package type it edits. */
const getPackageTypeSettings = (packageType: PackageType): IPackageTypeSettings => {
  if (packageType === 'TIME_OR_CREDIT_BASED') {
    return { allowsProration: false, allowsFollowUpPackage: false, renewsOnCredits: true };
  }
  return { allowsProration: true, allowsFollowUpPackage: true, renewsOnCredits: false };
};

interface ITimeBasedPackageFormProps {
  state: PackageDrawerState;
  setState: React.Dispatch<React.SetStateAction<PackageDrawerState>>;
  setPackageId: React.Dispatch<React.SetStateAction<string>>;
  formId: string;
  packageHook: UseQueryResult<{
    data: PackageRetrieve;
  }>;
  createPackageMutation: UseMutationResult<AxiosResponse<Package>, AxiosError<ErrorMessage>, PackageWrite>;
  updatePackageMutation: UseMutationResult<AxiosResponse<Package>, AxiosError<ErrorMessage>, PackageWrite>;
  permission?: boolean;
  packageType?: PackageType;
}

const TimeBasedPackageForm = ({
  state,
  setState,
  setPackageId,
  formId,
  packageHook,
  createPackageMutation,
  updatePackageMutation,
  permission = true,
  packageType,
}: ITimeBasedPackageFormProps): JSX.Element => {
  const { classes, theme } = useStyles();
  const staffTracking = useStaffTracking();
  const [isAdvancedSettingsOpen, { toggle: toggleAdvancedSettings }] = useDisclosure(false);
  const disablePackageForm = state !== 'create-package' && state !== 'edit-package';
  const [billingPlanOptions, setBillingPlanOptions] = useState<SelectItem[]>([]);
  const { session } = useAuth();
  const resolvedType: PackageType = packageHook.data?.data?.type ?? packageType ?? 'TIME_BASED';
  const typeSettings = getPackageTypeSettings(resolvedType);

  const form = useForm<TimeBasedPackageFormValues>({
    initialValues: {
      packageId: uuidv4(),
      name: '',
      description: '',
      packageValidityAmount: 1,
      packageValidityPeriod: 'week',
      packageCategory: '',
      allowProration: 'Off',
      autoRenew: 'Off',
      contract: {
        value: '',
        documentId: '',
        label: '',
      },
      contractOnRenew: false,
      athleteStatusOnStart: 'No status change',
      athleteStatusOnEnd: 'No status change',
      allowRollover: 'Off',
      rolloverExpires: 'Do Not Expire',
      rolloverInterval: 'week',
      rolloverIntervalCount: 1,
      isRolloverLimited: 'Unlimited',
      rolloverLimit: 1,
      limitQuantity: 'Off',
      maxSalesAllowed: 1,
      oneTimePurchase: 'Off',
      setSpecificStartDate: 'Off',
      startDate: new Date(),
      invoiceTrigger: 'at_package_start',
      includeFollowUpPackage: false,
      successorPackage: null,
      successorBillingPlan: null,
      clickToCancelEnabled: 'Off',
      attendanceRules: [
        {
          attendanceRuleId: undefined,
          isInfiniteSessions: false,
          credits: 1,
          intervalCount: 1,
          interval: 'week',
          validityIntervalCount: 1,
          validityInterval: 'week',
          delayIntervalCount: null,
          delayInterval: null,
          kiloClassTypes: [],
          appointmentTypes: [],
          perOrPack: 'per',
          delayStart: false,
          oldKiloClassTypeSet: new Set(),
          oldAppointmentTypeSet: new Set(),
          eventTypeState: 'same',
        },
      ],
    },
    validate: {
      name: value =>
        value.length > 0 && value.length <= 50 ? null : 'Your display name must be between 1-50 characters',
      description: value =>
        value && value.length > 250 ? 'Ensure this field has no more than 250 characters.' : null,
      autoRenew: isNotEmpty('Auto Renew cannot be empty'),
      contract: values =>
        values.label && !values.documentId ? 'Select an option or clear the field' : null,
      athleteStatusOnStart: value =>
        athleteStatusOptions.onStart.includes(value) ? null : 'Please select a valid status',
      athleteStatusOnEnd: (value, values) => {
        if (!athleteStatusOptions.onEnd.includes(value)) {
          return 'Please select a valid status';
        }
        return validateAthleteEndStatus(values);
      },
      // packageCategory: value =>
      //   state === 'create-package' && !value ? 'Please select a package category' : null,
      attendanceRules: {
        kiloClassTypes: (_, values, path) =>
          validateEventTypes(values, path) ??
          (typeSettings.renewsOnCredits
            ? validateHasLimitedAttendanceRule(values, TIME_OR_CREDIT_BASED_LIMITED_RULE_MESSAGE)
            : null),
        validityIntervalCount: (value, values, path) => validateValidityIntervalCount(value, values, path),
      },
    },
    transformValues: (values: TimeBasedPackageFormValues): TimeBasedPackageFormValues => ({
      ...values,
      rolloverInterval:
        values.allowRollover === 'On' && values.rolloverExpires === 'Expires'
          ? values.rolloverInterval
          : null,
      rolloverIntervalCount:
        values.allowRollover === 'On' && values.rolloverExpires === 'Expires'
          ? values.rolloverIntervalCount
          : null,
      rolloverLimit:
        values.allowRollover === 'On' && values.isRolloverLimited === 'Limited to'
          ? values.rolloverLimit
          : null,
      autoRenew: values.oneTimePurchase === 'On' ? 'Off' : values.autoRenew,
      limitQuantity:
        values.autoRenew === 'On' || values.allowProration === 'On' ? 'Off' : values.limitQuantity,
      maxSalesAllowed: values.limitQuantity === 'On' ? values.maxSalesAllowed : null,
      setSpecificStartDate:
        values.autoRenew === 'On' || values.allowProration === 'On' ? 'Off' : values.setSpecificStartDate,
      startDate: values.setSpecificStartDate === 'On' ? values.startDate : null,
    }),
  });

  const { data: successorPackageData, isSuccess: isSuccessorPackageData } = usePackage(
    form.values.successorPackage || '',
  );

  const hasEventAdded = (): boolean => {
    if (state === 'edit-package' && packageHook.data?.data.attendanceRules) {
      return form.values.attendanceRules.some(rule => rule.eventTypeState === 'superset');
    }
    return false;
  };

  const hasRolloverChanged = (): boolean => {
    if (state === 'edit-package' && packageHook.data?.data) {
      return (
        form.isDirty('allowRollover') ||
        form.isDirty('rolloverExpires') ||
        form.isDirty('isRolloverLimited') ||
        form.isDirty('rolloverLimit') ||
        form.isDirty('rolloverInterval') ||
        form.isDirty('rolloverIntervalCount')
      );
    }
    return false;
  };

  const hasAttendanceRuleChanged = (): boolean => {
    if (state === 'edit-package' && packageHook.data?.data.attendanceRules) {
      if (form.values.attendanceRules.length !== packageHook.data?.data.attendanceRules.length) {
        return true;
      }
      const arChanged = form.values.attendanceRules.some(rule => rule.eventTypeState === 'incompatible');
      if (arChanged) {
        return true;
      } else {
        return form.values.attendanceRules.some((rule, index) => {
          const originalRule = packageHook.data?.data.attendanceRules[index];
          if (!originalRule) return true; // if the original rule does not exist, it means its been removed
          const perOrPack = originalRule.interval === null ? 'pack' : 'per';
          return (
            rule.credits !== originalRule.credits ||
            rule.interval !== originalRule.interval ||
            rule.delayInterval !== originalRule.delayInterval ||
            rule.validityInterval !== originalRule.validityInterval ||
            rule.intervalCount !== originalRule.intervalCount ||
            rule.delayIntervalCount !== originalRule.delayIntervalCount ||
            rule.validityIntervalCount !== originalRule.validityIntervalCount ||
            rule.perOrPack !== perOrPack
          );
        });
      }
    }
    return false;
  };

  const changePackageValidity = (event: Period): void => {
    form.setValues(prev => {
      return {
        ...prev,
        packageValidityPeriod: event,
      };
    });
    form.setValues(prev => {
      if (prev && prev.attendanceRules) {
        return {
          ...prev,
          attendanceRules: prev.attendanceRules.map(rule => {
            return {
              ...rule,
              interval: rule.credits && rule.intervalCount !== null ? event : null,
              delayInterval: rule.delayStart ? event : null,
              validityInterval: event,
            };
          }),
        };
      }
      return { ...prev };
    });
  };

  const handleAutoRenewChange = (isAutoRenew: string): void => {
    form.setFieldValue('autoRenew', isAutoRenew);
    if (isAutoRenew === 'On') {
      form.setValues(prev => {
        if (prev && prev.attendanceRules) {
          return {
            ...prev,
            limitQuantity: 'Off',
            setSpecificStartDate: 'Off',
            includeFollowUpPackage: false,
            attendanceRules: prev.attendanceRules.map(rule => {
              return {
                ...rule,
                delayStart: false,
                delayInterval: null,
                delayIntervalCount: null,
              };
            }),
          };
        }
        return { ...prev };
      });
    }
  };

  const handleOneTimePurchaseChange = (isOneTime: string): void => {
    form.setFieldValue('oneTimePurchase', isOneTime as 'On' | 'Off');
    if (isOneTime === 'On') {
      form.setFieldValue('autoRenew', 'Off');
    }
  };

  const handleProrationChange = (isProrated: string): void => {
    form.setFieldValue('allowProration', isProrated);
    if (isProrated === 'On') {
      changePackageValidity(Period.month);
      form.setValues(prev => {
        if (prev && prev.attendanceRules) {
          return {
            ...prev,
            setSpecificStartDate: 'Off',
            limitQuantity: 'Off',
            attendanceRules: prev.attendanceRules.map(rule => {
              return {
                ...rule,
                delayStart: false,
                delayInterval: null,
                delayIntervalCount: null,
                isInfiniteSessions: rule.isInfiniteSessions,
                intervalCount: rule.perOrPack === 'pack' ? null : 1,
                credits: rule.credits,
                validityIntervalCount: rule.perOrPack === 'pack' ? 1 : rule.validityIntervalCount,
              };
            }),
          };
        }
        return { ...prev };
      });
    }
  };

  const handleSetSpecificStartDateChange = (setStartDate: 'On' | 'Off'): void => {
    if (setStartDate === 'On') {
      form.setValues(prev => {
        if (prev && prev.attendanceRules) {
          return {
            ...prev,
            attendanceRules: prev.attendanceRules.map(rule => {
              return {
                ...rule,
                delayStart: false,
                delayInterval: null,
                delayIntervalCount: null,
              };
            }),
          };
        }
        return { ...prev };
      });
    }
    form.setFieldValue('setSpecificStartDate', setStartDate);
  };

  const handleContractChange = (item: DocumentAutoCompleteValue | null): void => {
    const newValue = {
      value: item?.value || '',
      label: item?.label || '',
      documentId: item?.documentId || '',
    };
    form.setFieldValue('contract', newValue);
    if (item === null) {
      form.setFieldValue('contractOnRenew', false);
    }
  };

  const handleSuccessorPackageChange = (value: string): void => {
    form.setFieldValue('successorPackage', value);
    if (value) {
      form.setFieldValue('successorBillingPlan', null);
    }
  };

  function handleClickToCancelEnabledChange(value: 'On' | 'Off'): void {
    form.setFieldValue('clickToCancelEnabled', value);
  }

  const addAttendanceRule = (): void => {
    form.insertListItem('attendanceRules', {
      isInfiniteSessions: false,
      credits: 1, // should only be null if unlimited
      intervalCount: 1,
      interval: form.values.packageValidityPeriod,
      validityIntervalCount: 1,
      validityInterval: form.values.packageValidityPeriod,
      delayIntervalCount: null,
      delayInterval: null,
      kiloClassTypes: [],
      appointmentTypes: [],
      perOrPack: 'per',
      delayStart: false,
    });
  };

  const handleSubmit = (values: TimeBasedPackageFormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();

    const payload: PackageWrite = {
      packageId: values.packageId,
      type: resolvedType,
      name: values.name,
      description: values.description,
      allowProration: typeSettings.allowsProration && values.allowProration === 'On',
      autoRenew: values.autoRenew === 'On',
      contract: values.contract?.documentId || null,
      contractOnRenew: values.contractOnRenew,
      packageCategory: values.packageCategory || null,
      athleteStatusOnStart:
        values.athleteStatusOnStart === 'No status change' ? null : values.athleteStatusOnStart,
      athleteStatusOnEnd:
        values.athleteStatusOnEnd === 'No status change' ? null : values.athleteStatusOnEnd,
      allowRollover: values.allowRollover === 'On',
      rolloverInterval: values.rolloverInterval,
      rolloverIntervalCount: values.rolloverIntervalCount,
      rolloverLimit: values.rolloverLimit,
      maxSalesAllowed: values.limitQuantity === 'On' ? values.maxSalesAllowed : null,
      oneTimePurchase: values.oneTimePurchase === 'On',
      startDate: values.setSpecificStartDate === 'On' ? dayjs(values.startDate).format('YYYY-MM-DD') : null,
      invoiceTrigger: values.setSpecificStartDate === 'On' ? values.invoiceTrigger : null,
      successorPackage:
        typeSettings.allowsFollowUpPackage && values.includeFollowUpPackage ? values.successorPackage : null,
      successorBillingPlan:
        typeSettings.allowsFollowUpPackage && values.includeFollowUpPackage
          ? values.successorBillingPlan
          : null,
      attendanceRules: values.attendanceRules.map(rule => ({
        attendanceRuleId: rule.attendanceRuleId ?? null,
        credits: rule.credits,
        interval: rule.interval,
        intervalCount: rule.intervalCount,
        delayInterval: rule.delayInterval,
        delayIntervalCount: rule.delayIntervalCount,
        validityInterval: rule.validityInterval,
        validityIntervalCount: rule.validityIntervalCount,
        kiloClassTypes: rule.kiloClassTypes.map(v => v.eventTypeId),
        appointmentTypes: rule.appointmentTypes.map(v => v.eventTypeId),
      })),
      clickToCancelEnabled: values.clickToCancelEnabled === 'On',
    };

    const isCreatingNewPackage = state === 'create-package' && !packageHook?.data;
    const isUpdatingExistingPackage =
      state === 'edit-package' || (state === 'create-package' && !!packageHook?.data);

    if (isCreatingNewPackage) {
      createPackageMutation.mutate(payload, {
        onError: error => {
          errorNotification('Package creation failed', error.response?.data.message);
        },
        onSuccess: response => {
          staffTracking.trackPackageCreated(
            response.data.packageId,
            response.data.name,
            response.data.type,
            values.autoRenew === 'On',
            values.oneTimePurchase === 'On',
            values.packageValidityAmount,
            values.packageValidityPeriod,
            values.athleteStatusOnStart,
            values.athleteStatusOnEnd,
          );
          form.reset();
          setState('create-billing');
          setPackageId(response.data.packageId);
        },
      });
    }

    if (isUpdatingExistingPackage) {
      updatePackageMutation.mutate(payload, {
        onError: error => {
          errorNotification('Package update failed', error.response?.data.message);
        },
        onSuccess: response => {
          form.resetDirty();
          if (state === 'create-package') {
            setState('create-billing');
            setPackageId(response.data.packageId);
          } else {
            setState('view-package');
            successNotification('Package update successful', `${response.data.name} successfully updated.`);
          }
        },
      });
    }
  };

  useShallowEffect(() => {
    if (state !== 'create-package' && packageHook.data?.data && packageHook.isSuccess) {
      form.setValues({
        packageId: packageHook.data.data.packageId,
        name: packageHook.data.data.name,
        description: packageHook.data.data.description || '',
        packageValidityAmount: computeTotalValidity(packageHook.data.data.attendanceRules),
        packageValidityPeriod: packageHook.data.data.attendanceRules[0].validityInterval as string,
        packageCategory: packageHook.data.data.packageCategory || '',
        allowProration: packageHook.data.data.allowProration ? 'On' : 'Off',
        autoRenew: packageHook.data.data.autoRenew ? 'On' : 'Off',
        contract: {
          value: packageHook.data.data.contract?.documentId || '',
          documentId: packageHook.data.data.contract?.documentId || '',
          label: packageHook.data.data.contract?.description || '',
        },
        contractOnRenew: packageHook.data.data.contractOnRenew,
        athleteStatusOnStart: packageHook.data.data?.athleteStatusOnStart || 'No status change',
        athleteStatusOnEnd: packageHook.data.data?.athleteStatusOnEnd || 'No status change',
        allowRollover: packageHook.data.data.allowRollover ? 'On' : 'Off',
        rolloverInterval: packageHook.data.data.rolloverInterval || 'week',
        rolloverIntervalCount: packageHook.data.data.rolloverIntervalCount || 1,
        rolloverLimit: packageHook.data.data.rolloverLimit || 1,
        rolloverExpires: packageHook.data.data.rolloverIntervalCount ? 'Expires' : 'Do Not Expire',
        isRolloverLimited: packageHook.data.data.rolloverLimit ? 'Limited to' : 'Unlimited',
        limitQuantity: packageHook.data.data.maxSalesAllowed ? 'On' : 'Off',
        maxSalesAllowed: packageHook.data.data.maxSalesAllowed || 1,
        oneTimePurchase: packageHook.data.data.oneTimePurchase ? 'On' : 'Off',
        setSpecificStartDate: packageHook.data.data.startDate ? 'On' : 'Off',
        startDate: packageHook.data.data.startDate
          ? parseDateTimeToDate(packageHook.data.data.startDate, session?.gym?.timezone)
          : new Date(),
        invoiceTrigger: packageHook.data.data.invoiceTrigger || 'at_package_start',
        includeFollowUpPackage: packageHook.data.data.successorPackage ? true : false,
        successorPackage: packageHook.data.data?.successorPackage?.packageId || '',
        successorBillingPlan: packageHook.data.data?.successorBillingPlan?.billingPlanId || '',
        clickToCancelEnabled: packageHook.data.data.clickToCancelEnabled ? 'On' : 'Off',
        attendanceRules: packageHook.data.data.attendanceRules.map(ar => {
          return {
            ...ar,
            attendanceRuleId: ar.attendanceRuleId,
            kiloClassTypes: ar.kiloClassTypes.map(ct => {
              return {
                eventType: 'kilo_class_type',
                eventTypeId: ct.kiloClassTypeId,
                eventTypeName: ct.name,
              };
            }),
            appointmentTypes: ar.appointmentTypes.map(ct => {
              return {
                eventType: 'appointment_type',
                eventTypeId: ct.appointmentTypeId,
                eventTypeName: ct.name,
              };
            }),
            isInfiniteSessions: ar.credits === null,
            perOrPack: ar.interval === null ? 'pack' : 'per',
            delayStart: ar.delayInterval !== null,
            oldKiloClassTypeSet: new Set(ar.kiloClassTypes.map(ct => ct.kiloClassTypeId)),
            oldAppointmentTypeSet: new Set(ar.appointmentTypes.map(ct => ct.appointmentTypeId)),
            eventTypeState: 'same',
          };
        }),
      });
      form.resetDirty();
    }
  }, [packageHook.data?.data, state]);

  useEffect(() => {
    const totalValidityCount = computeTotalValidity(form.values.attendanceRules);
    form.setValues(prev => ({ ...prev, packageValidityAmount: totalValidityCount }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.attendanceRules]);

  useEffect(() => {
    if (isSuccessorPackageData && successorPackageData) {
      const billingPlans = successorPackageData.data.billingPlans.map(billingPlan => ({
        value: billingPlan.billingPlanId,
        label: billingPlan.name,
      }));
      setBillingPlanOptions(billingPlans);
    }
  }, [successorPackageData, isSuccessorPackageData]);

  return (
    <form id={formId} onSubmit={form.onSubmit(handleSubmit)} className={classes.packageForm}>
      <LoadingOverlay visible={state !== 'create-package' && packageHook.isLoading} />
      <Stack px={px(theme.spacing.xl) * 2} py='md' bg={disablePackageForm ? 'gray.1' : undefined}>
        <Group>
          <Title order={4}>Package Details</Title>
          {packageHook.data?.data.isArchived && (
            <Badge color='red' variant='filled' size='sm'>
              Archived
            </Badge>
          )}
        </Group>
        <div className={classes.top}>
          <Input.Label required ta='right' my='0.25rem'>
            Name
          </Input.Label>
          <TextInput
            {...form.getInputProps('name')}
            size='xs'
            required
            placeholder='Enter name'
            disabled={disablePackageForm || !permission}
            data-cy='inputNamePackage'
          />

          <Input.Label ta='right' my='0.5rem'>
            Description
          </Input.Label>
          <Textarea
            {...form.getInputProps('description')}
            maxLength={250}
            size='xs'
            placeholder='Enter description'
            disabled={disablePackageForm || !permission}
            data-cy='inputDescriptionPackage'
            error={
              !disablePackageForm && form.isDirty('description') && form.values.description.length === 250
                ? 'Maximum 250 characters reached'
                : undefined
            }
          />
          <Input.Label ta='right' my='0.25rem'>
            Package Category
          </Input.Label>
          <Group>
            <PackageCategorySelect
              {...form.getInputProps('packageCategory')}
              size='xs'
              placeholder='Select package category'
              disabled={disablePackageForm || !permission}
              // required={true}
            />
            <HelpButton width={500} position='right'>
              <Text size='sm'>To categorize this package for reporting and organizational purposes.</Text>
            </HelpButton>
          </Group>

          {typeSettings.allowsProration && (
            <>
              <Input.Label ta='right' my='0.25rem'>
                Proration
              </Input.Label>
              <Group>
                <Select
                  {...form.getInputProps('allowProration')}
                  size='xs'
                  data={['On', 'Off']}
                  disabled={state === 'edit-package' || disablePackageForm || !permission}
                  onChange={handleProrationChange}
                  data-cy='allowProRationOption'
                />
                <HelpButton width={500} position='right'>
                  <Text size='sm'>
                    To accurately prorate invoices and event credits, many of the customizable options of a
                    package are not editable. Credits and invoices must occur in monthly cycles for the same
                    total length of time.
                  </Text>
                </HelpButton>
                {form.values.allowProration === 'On' && (
                  <WarningButton width={500} position='right'>
                    <Text size='sm'>
                      Delay Start Date cannot be enabled while Proration is ON. Please disable Proration ON
                      to use this setting.
                    </Text>
                  </WarningButton>
                )}
              </Group>
            </>
          )}

          <Input.Label ta='right' my='0.25rem'>
            One-Time Purchase Limit
          </Input.Label>
          <Group>
            <Select
              {...form.getInputProps('oneTimePurchase')}
              size='xs'
              data={['On', 'Off']}
              disabled={disablePackageForm || !permission}
              onChange={handleOneTimePurchaseChange}
              data-cy='oneTimePurchaseOption'
            />
            <HelpButton width={500} position='right'>
              <Text size='sm'>
                Restricts this package to a single purchase per athlete. Athletes who have already purchased
                it will not see it in the app or sales portal, and staff will not see it in checkout for that
                athlete. Auto Renew is turned off and locked while this is on, since a renewal would count as
                a second purchase. A follow-up package is still allowed.
              </Text>
            </HelpButton>
          </Group>

          <Input.Label ta='right' my='0.25rem'>
            Auto Renew
          </Input.Label>
          <Stack>
            <Group>
              <Select
                {...form.getInputProps('autoRenew')}
                onChange={handleAutoRenewChange}
                size='xs'
                w={220}
                required
                data={['On', 'Off']}
                disabled={
                  disablePackageForm ||
                  form.values.includeFollowUpPackage ||
                  form.values.oneTimePurchase === 'On' ||
                  !permission
                }
                data-cy='autoRenewOption'
              />
              {form.values.oneTimePurchase === 'On' && (
                <WarningButton width={500} position='right'>
                  <Text size='sm'>
                    Auto Renew cannot be enabled while the One-Time Purchase Limit is on, because a renewal
                    would create a second purchase of this package.
                  </Text>
                </WarningButton>
              )}
              <HelpButton width={500} position='right'>
                <Text size='sm'>
                  Auto Renew determines if this package should be automatically renewed. This setting can be
                  overridden when applying a package to an athlete. If Auto Renew is off a follow-up package
                  can be included for the package to rollover into a different one.
                </Text>
              </HelpButton>
            </Group>
            {typeSettings.allowsFollowUpPackage && form.values.autoRenew === 'Off' && (
              <Checkbox
                {...form.getInputProps('includeFollowUpPackage')}
                size='xs'
                label='Include Follow-up Package'
                checked={form.values.includeFollowUpPackage}
                disabled={disablePackageForm || !permission}
              />
            )}
            {typeSettings.allowsFollowUpPackage && form.values.includeFollowUpPackage && (
              <Group>
                <PackageSelect
                  {...form.getInputProps('successorPackage')}
                  packageId={form.values.packageId}
                  onChange={handleSuccessorPackageChange}
                  size='xs'
                  placeholder='Select package'
                  required
                  excludeCreditBased
                  disabled={disablePackageForm || !permission}
                />
                <Select
                  {...form.getInputProps('successorBillingPlan')}
                  data={billingPlanOptions}
                  size='xs'
                  placeholder='Select billing plan'
                  clearable
                  searchable
                  required
                  disabled={disablePackageForm || !form.values.successorPackage || !permission}
                />
              </Group>
            )}
          </Stack>
        </div>
        <Group>
          <Input.Label>Allow Athlete Click-to-Cancel in the Athlete App</Input.Label>
          <Select
            {...form.getInputProps('clickToCancelEnabled')}
            size='xs'
            w={220}
            required
            data={['On', 'Off']}
            disabled={disablePackageForm || !permission}
            defaultValue={form.values.clickToCancelEnabled ? 'On' : 'Off'}
            data-cy='clickToCancelEnabledOption'
            onChange={handleClickToCancelEnabledChange}
          />
        </Group>
        <div className={classes.top}>
          <Input.Label ta='right' my='0.25rem'>
            Contract
          </Input.Label>
          <Group>
            <DocumentAutoComplete
              {...form.getInputProps('contract')}
              size='xs'
              placeholder='Select contract'
              type='contract'
              isTemplate
              value={form.values.contract}
              onChange={handleContractChange}
              disabled={disablePackageForm || !permission}
              data-cy='selectContractPackageForm'
            />
            <Checkbox
              {...form.getInputProps('contractOnRenew')}
              size='xs'
              label='Generate new contract upon package renew'
              checked={form.values.contractOnRenew}
              disabled={disablePackageForm || !form.values.contract.documentId || !permission}
            />
            <HelpButton width={500} position='right'>
              <Text size='sm'>
                Linking a contract template to a package will create a contract for the athlete when a
                package is applied. By default, a contract will only be generated for the first package in a
                chain of renewals. Checking the checkbox will generate a new contract each time the package
                renews.
              </Text>
            </HelpButton>
            {form.isDirty('contract') && state === 'edit-package' && (
              <WarningButton width={500} position='right'>
                <Text size='sm'>
                  Modifying the contract linked to a package will not affect packages currently attached to
                  athletes. This contract will be applied for all new packages and any renewals if contracts
                  are generated upon renewal.
                </Text>
              </WarningButton>
            )}
          </Group>

          <Input.Label ta='right' my='0.25rem'>
            Athlete Status
          </Input.Label>
          <Group>
            <Input.Label>Upon Start</Input.Label>
            <AthleteStatusSelect
              {...form.getInputProps('athleteStatusOnStart')}
              size='xs'
              disabled={disablePackageForm || !permission}
              type='onStart'
              data-cy='athleteStatusUponStart'
            />
            <Input.Label>Upon End</Input.Label>
            <AthleteStatusSelect
              {...form.getInputProps('athleteStatusOnEnd')}
              size='xs'
              disabled={disablePackageForm || !permission}
              type='onEnd'
              excludeValue={form.values.athleteStatusOnStart}
              data-cy='athleteStatusUponEnd'
            />
            <HelpButton width={500} position='right'>
              <Text size='sm'>
                These settings allow you to control what status gets applied to the athlete at the start date
                of a package and what status the athlete should transition to at the end of package. Package
                renewals will not cause status changes. Only the first package and the end of a chain will
                trigger these status changes.
                <br />
                <br />
                These settings can be overridden when applying a package to an athlete.
              </Text>
            </HelpButton>
          </Group>
        </div>

        <ExpandableSection
          title='Advanced Settings'
          opened={isAdvancedSettingsOpen}
          handleToggle={toggleAdvancedSettings}
          isDisabled={false}
        >
          <Stack>
            <Group>
              <Input.Label ta='right' my='0.25rem'>
                Enable Rollover Credits
              </Input.Label>
              <Select
                {...form.getInputProps('allowRollover')}
                size='xs'
                data={['On', 'Off']}
                disabled={disablePackageForm || !permission}
                defaultValue={form.values.allowRollover ? 'On' : 'Off'}
                data-cy='allowRolloverOption'
              />
              <HelpButton width={500} position='right'>
                <Text size='sm'>
                  This setting allows credits to rollover. If a credit dispensation expires with unused
                  credits, a new credit dispensation will be created for the remaining credits.
                </Text>
                {typeSettings.renewsOnCredits && (
                  <Text size='sm'>
                    Rollover credits apply only when the package renews by time. If renewal was triggered by
                    credits running out there is nothing left to roll over.
                  </Text>
                )}
              </HelpButton>
              {hasRolloverChanged() && (
                <WarningButton width={500} position='right'>
                  <Stack p='sm'>
                    <Title order={5}>Warning!</Title>
                    <Text size='sm'>The rollover settings have been modified for this package.</Text>
                    <Text size='sm'>
                      These changes will not modify packages attached to athletes in the ENDED, CANCELLED,
                      ACTIVE, or HOLD states. These changes will be applied to UPCOMING packages, renewals,
                      and new packages.
                    </Text>
                    <Text size='sm'>
                      UPCOMING packages will be deleted and reapplied to reflect the new rollover settings
                      unless certain <i>*billing events</i> have occurred.
                    </Text>
                    <Text size='sm'>
                      <i>*Billing events</i>: invoices generated from this package have had any attempted
                      charges
                    </Text>
                  </Stack>
                </WarningButton>
              )}
            </Group>
            {form.values.allowRollover == 'On' && (
              <Stack>
                <Group>
                  <Input.Label ta='right' my='0.25rem'>
                    Rollover Credits
                  </Input.Label>
                  <Select
                    {...form.getInputProps('rolloverExpires')}
                    size='xs'
                    data={['Expires', 'Do Not Expire']}
                    disabled={disablePackageForm || !permission}
                    data-cy='rolloverExpires'
                  />
                  {form.values.rolloverExpires === 'Expires' && (
                    <Group>
                      <Input.Label ta='right' my='0.25rem'>
                        after
                      </Input.Label>
                      <NumberInput
                        {...form.getInputProps('rolloverIntervalCount')}
                        size='xs'
                        w={50}
                        min={1}
                        disabled={disablePackageForm || !permission}
                        data-cy='rolloverIntervalCount'
                      />
                      <Select
                        {...form.getInputProps('rolloverInterval')}
                        size='xs'
                        data={PERIODOPTIONS}
                        disabled={disablePackageForm || !permission}
                        data-cy='rolloverInterval'
                      />
                    </Group>
                  )}
                  <HelpButton width={500} position='right'>
                    <Text size='sm'>
                      This setting controls the expiration date of the new credit dispensation. The dispense
                      date will be directly after the original credit dispensation&apos;s expiration date.
                    </Text>
                  </HelpButton>
                </Group>
              </Stack>
            )}
            {form.values.allowRollover == 'On' && (
              <Stack>
                <Group>
                  <Input.Label ta='right' my='0.25rem'>
                    Rollover Credit Limit
                  </Input.Label>
                  <Select
                    {...form.getInputProps('isRolloverLimited')}
                    size='xs'
                    data={['Limited to', 'Unlimited']}
                    disabled={disablePackageForm || !permission}
                  />
                  {form.values.isRolloverLimited === 'Limited to' && (
                    <Group>
                      <NumberInput
                        {...form.getInputProps('rolloverLimit')}
                        size='xs'
                        w={50}
                        defaultValue={1}
                        min={1}
                        disabled={disablePackageForm || !permission}
                      />
                      <Input.Label>credits</Input.Label>
                    </Group>
                  )}
                  <HelpButton width={500} position='right'>
                    <Text size='sm'>The maximum number of unused credits that are allowed to rollover.</Text>
                  </HelpButton>
                </Group>
              </Stack>
            )}
            <Divider />
            <Group>
              <Input.Label ta='right' my='0.25rem'>
                Limited Quantity
              </Input.Label>
              <Select
                {...form.getInputProps('limitQuantity')}
                size='xs'
                data={['On', 'Off']}
                disabled={
                  form.values.autoRenew === 'On' ||
                  form.values.allowProration === 'On' ||
                  disablePackageForm ||
                  !permission
                }
                defaultValue={'Off'}
              />
              <HelpButton width={500} position='right'>
                <Text size='sm'>Use this setting to limit the amount of packages that can be sold.</Text>
              </HelpButton>
              {form.values.autoRenew === 'On' && (
                <WarningButton width={500} position='right'>
                  <Text size='sm'>
                    Limited Quantity cannot be enabled while Auto-Renew is ON. Please disable Auto-Renew ON
                    to use this setting. Delay Start Date cannot be enabled while Auto-Renew is ON. Please
                    disable Auto-Renew ON to use this setting.
                  </Text>
                </WarningButton>
              )}
            </Group>
            {form.values.limitQuantity === 'On' && (
              <Group>
                <Input.Label ta='right' my='0.25rem'>
                  Limit Package Sales
                </Input.Label>
                <NumberInput
                  {...form.getInputProps('maxSalesAllowed')}
                  size='xs'
                  min={1}
                  max={1000}
                  defaultValue={1}
                  disabled={disablePackageForm || !permission}
                />
                <HelpButton width={500} position='right'>
                  <Text size='sm'>The maximum number of packages that can be sold of this type.</Text>
                </HelpButton>
              </Group>
            )}
            <Divider />
            <Group>
              <Input.Label ta='right' my='0.25rem'>
                Set Specific Start Date
              </Input.Label>
              <Select
                {...form.getInputProps('setSpecificStartDate')}
                size='xs'
                data={['On', 'Off']}
                defaultValue={'Off'}
                onChange={handleSetSpecificStartDateChange}
                disabled={
                  form.values.allowProration === 'On' ||
                  form.values.autoRenew === 'On' ||
                  disablePackageForm ||
                  !permission
                }
              />
              <HelpButton width={500} position='right'>
                <Text size='sm'>
                  Use this setting to enable pre-sales for a program that starts in the future.
                </Text>
              </HelpButton>
            </Group>
            {form.values.setSpecificStartDate === 'On' && (
              <>
                <Group>
                  <Input.Label ta='right' my='0.25rem'>
                    Package Start Date
                  </Input.Label>
                  <DatePickerInput
                    {...form.getInputProps('startDate')}
                    size='xs'
                    w={150}
                    disabled={
                      !!packageHook.data?.data.hasAthletePackages || disablePackageForm || !permission
                    }
                    minDate={new Date()}
                    defaultValue={new Date()}
                  />
                  <HelpButton width={500} position='right'>
                    <Text size='sm'>The date that this package will start.</Text>
                  </HelpButton>
                  {form.isDirty('startDate') && state === 'edit-package' && (
                    <WarningButton width={500} position='right'>
                      <Stack p='sm'>
                        <Text size='sm'>Package start date cannot change once package has been sold.</Text>
                      </Stack>
                    </WarningButton>
                  )}
                </Group>
                <Group>
                  <Input.Label ta='right' my='0.25rem'>
                    Billing Plan Start Date
                  </Input.Label>
                  <Select
                    {...form.getInputProps('invoiceTrigger')}
                    size='xs'
                    w={230}
                    data={[
                      { value: 'at_package_start', label: 'Match package start date' },
                      { value: 'immediately', label: 'Immediately Charge First Invoice' },
                    ]}
                    defaultValue={'at_package_start'}
                    disabled={disablePackageForm || !permission}
                  />
                  <HelpButton width={500} position='right'>
                    <Text size='sm'>
                      Match Package Start Date means that the Billing Plan will start on the day that the
                      package is set to start.
                      <br></br>
                      <br></br>
                      Charge First Invoice Immediately means that the first invoice will be charged when the
                      package is purchased. The rest of the billing plan installments will align with the
                      Package Start Date.
                    </Text>
                  </HelpButton>
                </Group>
              </>
            )}
          </Stack>
        </ExpandableSection>
      </Stack>

      <Divider />

      <Stack
        spacing='lg'
        px={px(theme.spacing.xl) * 2}
        py='md'
        bg={disablePackageForm ? 'gray.1' : undefined}
        sx={{ flexGrow: 1 }}
      >
        <Stack spacing={0}>
          <Group>
            <Title order={4}>Attendance Rules</Title>
            {form.isDirty('attendanceRules') && state === 'edit-package' && (
              <WarningButton width={500} position='right'>
                <Stack p='sm'>
                  <Title order={5}>Warning!</Title>
                  <Text size='sm'>The attendance rules have been modified for this package.</Text>
                  <Text size='sm'>
                    These changes will not modify packages attached to athletes in the ENDED, CANCELLED,
                    ACTIVE, or HOLD states. These changes will be applied to UPCOMING packages, renewals, and
                    new packages.
                  </Text>
                  <Text size='sm'>
                    UPCOMING packages will be deleted and reapplied to reflect the new attendance rules
                    unless certain <i>*billing events</i> have occurred. Reservations linked to the upcoming
                    packages will be preserved.
                  </Text>
                  <Text size='sm'>
                    <i>*Billing events</i>: invoices generated from this package have had any attempted
                    charges
                  </Text>
                </Stack>
              </WarningButton>
            )}
          </Group>
          <Text size='sm' color='dimmed'>
            Grant access to events via rules. Rules will dispense credits in a repeating (per) or one-off
            behavior (pack).
          </Text>
        </Stack>

        <Group noWrap>
          <Input.Label ta='right' my='0.25rem'>
            Total Package Length
          </Input.Label>
          <NumberInput
            {...form.getInputProps('packageValidityAmount')}
            size='xs'
            w={50}
            disabled
            data-cy='packageLengthNumberInput'
          />
          <Select
            size='xs'
            data={PERIODOPTIONS}
            value={form.values.packageValidityPeriod}
            onChange={changePackageValidity}
            disabled={disablePackageForm || !permission || form.values.allowProration === 'On'}
            data-cy='packageLengthTimeInput'
          />
          <HelpButton width={500} position='right'>
            <Text size='sm'>
              The total package length is determined by the attendance rules. Whatever unit of time selected
              must be consistent across all attendance rules.
            </Text>
          </HelpButton>
        </Group>
        {typeSettings.renewsOnCredits && (
          <RenewalSummary
            attendanceRules={form.values.attendanceRules}
            lengthAmount={form.values.packageValidityAmount}
            lengthPeriod={form.values.packageValidityPeriod}
            autoRenews={form.values.autoRenew === 'On' && form.values.oneTimePurchase !== 'On'}
          />
        )}
        {hasEventAdded() && !hasAttendanceRuleChanged() && !hasRolloverChanged() && (
          <Group
            noWrap
            style={{
              outline: '2px solid #FFEA00',
              borderRadius: '10px',
              padding: '0.5rem',
              backgroundColor: '#FFFFC5',
            }}
          >
            <IconAlertTriangleFilled size={30} color='yellow' style={{ color: '#FFEA00' }} />
            <Text>
              You&apos;ve added new event types to this package. These changes will apply to all active and
              upcoming athlete packages immediately after saving.
            </Text>
          </Group>
        )}
        {(hasAttendanceRuleChanged() || (hasEventAdded() && hasRolloverChanged())) && (
          <Group
            noWrap
            style={{
              outline: '2px solid #FFEA00',
              borderRadius: '10px',
              padding: '0.5rem',
              backgroundColor: '#FFFFC5',
            }}
          >
            <IconAlertTriangleFilled size={30} color='yellow' style={{ color: '#FFEA00' }} />
            <Text>
              Changes to credits, validity period, rollover, or attendance rules will only apply to new
              purchases or upon renewal. Active athlete packages will remain unchanged.
            </Text>
          </Group>
        )}

        {form.values.attendanceRules.map((_, index) => (
          <TimeBasedAttendanceRuleCard
            key={index}
            disabled={disablePackageForm || !permission}
            index={index}
            form={form}
            packageType={resolvedType}
          />
        ))}
        {!disablePackageForm && (
          <ActionIcon variant='subtle' color='kiloBlue' onClick={addAttendanceRule} disabled={!permission}>
            <IconPlus />
          </ActionIcon>
        )}
      </Stack>
    </form>
  );
};
export default TimeBasedPackageForm;
